import { cn } from "../../lib";
import {
  DialogContentPrimitive,
  DialogOverlayPrimitive,
  DialogPortalPrimitive,
  DialogRootPrimitive,
  DialogTitlePrimitive,
} from "../../primitives";
import {
  sheetAnimationClasses,
  sheetOverlayClasses,
  sheetSideClasses,
  sheetSurfaceClasses,
} from "./lib";
import type { SheetProps } from "./types";

/**
 * Sheet — internal mobile off-canvas overlay for AppSidebar (web).
 *
 * Composes the **existing** `dialog` primitive (the repo's single
 * `@radix-ui/react-dialog` wrapper). Side positioning and the slide animation
 * are pure Tailwind on the dialog Content (`sheetSideClasses`), so there is no
 * new prop on the dialog primitive and no second radix-dialog wrap — shadcn's
 * standalone `sheet.tsx` is deliberately NOT vendored.
 *
 * Private to AppSidebar: not exported from `@luce/ui`. Kept in its own file so
 * that *if* a second, unrelated sheet consumer ever appears, promoting it to a
 * shared `molecules/sheet` is a one-commit file move rather than a rewrite.
 *
 * v1 scope: `side="left"` is the only case the AppSidebar mobile branch uses
 * and the only one verified. `right` exists for future reuse.
 *
 * @see docs/tickets/DS-042-sidebar-menu.md
 */
export const Sheet = ({
  open,
  onOpenChange,
  side = "left",
  accessibilityLabel = "Sidebar",
  className,
  testID,
  children,
}: SheetProps) => (
  <DialogRootPrimitive open={open} onOpenChange={onOpenChange}>
    <DialogPortalPrimitive>
      <DialogOverlayPrimitive className={sheetOverlayClasses} />
      <DialogContentPrimitive
        testID={testID}
        accessibilityLabel={accessibilityLabel}
        className={cn(
          sheetSideClasses[side],
          sheetSurfaceClasses,
          sheetAnimationClasses,
          className,
        )}
      >
        {/* Radix warns when a Dialog Content has no Title. The rail provides
            its own visible nav label, so the title is visually hidden. */}
        <DialogTitlePrimitive className="sr-only">{accessibilityLabel}</DialogTitlePrimitive>
        {children}
      </DialogContentPrimitive>
    </DialogPortalPrimitive>
  </DialogRootPrimitive>
);

Sheet.displayName = "AppSidebar.Sheet";
